import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs/operators';

import { LoaderService } from '@app/shared';
import { LcurrencyPipe } from '@app/shared/pipes';
import { AppService, ErrorHandlerService, PaymentService } from '@app/services';

export interface ModalResult {
  alertThreshold: number;
  maxLimit: number;
}

export interface ModalPublicProperties {
  alertThreshold?: number;
  maxLimit?: number;
  currency: string;
}

@Component({
  selector: 'app-api-usage-limits-modal',
  templateUrl: './api-usage-limits-modal.component.html',
  styleUrls: ['./api-usage-limits-modal.component.scss'],
  standalone: false
})
export class ApiUsageLimitsModalComponent implements OnInit {
  @Input() alertThreshold?: number;
  @Input() maxLimit?: number;
  @Input() currency: string;

  form: UntypedFormGroup;
  isLoading = false;

  constructor(
    public modal: NgbActiveModal,
    public appService: AppService,
    private formBuilder: UntypedFormBuilder,
    private paymentService: PaymentService,
    private loaderService: LoaderService,
    private errorHandlerService: ErrorHandlerService,
    private lcurrencyPipe: LcurrencyPipe
  ) {}

  ngOnInit() {
    this.form = this.formBuilder.group({
      alertThreshold: null,
      maxLimit: null
    });

    this.loadApiUsageLimits();
  }

  loadApiUsageLimits() {
    this.isLoading = true;
    this.paymentService
      .apiUsageLimits()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: limits => {
          if (limits) {
            this.form.patchValue({
              alertThreshold: limits.alert_threshold,
              maxLimit: limits.max_limit
            });
          }
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  save() {
    if (this.form.invalid || this.isLoading) return;

    this.form.markAllAsTouched();
    this.isLoading = true;
    this.loaderService.show();

    const alertThreshold = this.form.value.alertThreshold;
    const maxLimit = this.form.value.maxLimit;

    this.paymentService
      .setApiUsageLimits({
        alertThreshold: alertThreshold || alertThreshold === 0 ? parseFloat(alertThreshold) : null,
        maxLimit: maxLimit || maxLimit === 0 ? parseFloat(maxLimit) : null
      })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: data => this.modal.close({ alertThreshold: data.alert_threshold, maxLimit: data.max_limit } as ModalResult),
        error: error => {
          this.formatCurrencyInValidationError(error);
          this.errorHandlerService.handleValidation(this.form, error);
        }
      });
  }

  private formatCurrencyInValidationError(error: any) {
    const validation = error?.cause?.extensions?.validation;
    if (!validation) return;

    Object.keys(validation).forEach(key => {
      validation[key] = validation[key].map((err: string) => {
        if (!err.startsWith('must_be_greater_or_equal_than:')) return err;

        const parts = err.split(':');
        const num = parseFloat(parts[1].trim());
        if (!isNaN(num)) {
          return parts[0] + ':' + this.lcurrencyPipe.transform(num, this.appService.currencyCodeFor(this.currency));
        }
        return err;
      });
    });
  }
}
